import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Box,
  Typography,
  Slider,
  TextField,
  Button,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getCurrentMission } from '../../../dataModelHelpers';
import { cleanSet, nextPaint } from '../../../utils';
import { dispatchMissionUpdated } from '../../../missionEvents';
import { dispatchCoresNeedUpdate, dispatchCoreGridUpdated } from '../../../mapEvents';
import { EQUAL_AREA_DEFAULTS, type EqualAreaPlacerOptions } from '../../../services/zones/EqualAreaCorePlacer';
import { ISOLATED_BOOST_DEFAULT, SKIP_SMALL_SEGMENTS_DEFAULT } from '../../../services/zones/AutoCorePlacer';
import { SettingsAccessLevel } from '../../../types/types';

/**
 * The tuning panel is gated behind the Technician access level — the same gate
 * the app uses for its other technician/dev tooling. Never shown to normal
 * users; production ships no placement knobs.
 */
export function isCoreTuningEnabled(accessLevel: SettingsAccessLevel): boolean {
  return accessLevel === 'Technician';
}

interface CorePlacementTuningPanelProps {
  accessLevel: SettingsAccessLevel;
  /** Parent-controlled visibility — opened by the Auto-place button in Technician mode. */
  open: boolean;
  onClose: () => void;
  addCheckpoint: (name: string) => void;
}

type NumKey = Exclude<keyof Required<EqualAreaPlacerOptions>, 'centerBlend' | 'capRate' | 'spreadRate'>;

/** Numeric knobs rendered as small number fields (sliders handle the two bounded ones). */
const NUM_FIELDS: { key: NumKey; label: string; min: number; max: number; step: number }[] = [
  { key: 'targetSamples', label: 'targetSamples', min: 200, max: 12000, step: 100 },
  { key: 'maxSamples', label: 'maxSamples', min: 1000, max: 30000, step: 500 },
  { key: 'minStepMeters', label: 'minStep (m)', min: 0.1, max: 5, step: 0.1 },
  { key: 'maxStepMeters', label: 'maxStep (m)', min: 1, max: 20, step: 0.5 },
  { key: 'capInnerIters', label: 'capInnerIters', min: 1, max: 12, step: 1 },
  { key: 'capClamp', label: 'capClamp', min: 1, max: 10, step: 0.5 },
  { key: 'maxIterations', label: 'maxIterations', min: 5, max: 80, step: 1 },
  { key: 'convergenceMeters', label: 'convergence (m)', min: 0.05, max: 5, step: 0.05 },
];

/**
 * Floating dev panel to tune the equal-area placement kernel on a live mission.
 *
 * Opened from the Auto-place Cores button when the user is a Technician (regular
 * users run placement immediately). Its values thread into
 * `mission.autoPlaceCores(options)` — overriding the baked defaults — then it
 * wipes existing pending cores, recalculates, and redraws (the same flow as the
 * button, minus the confirm prompt, since you're iterating). Renders only when
 * `open` and {@link isCoreTuningEnabled}.
 */
export default function CorePlacementTuningPanel({
  accessLevel,
  open,
  onClose,
  addCheckpoint,
}: CorePlacementTuningPanelProps) {
  const [opts, setOpts] = useState<Required<EqualAreaPlacerOptions>>({ ...EQUAL_AREA_DEFAULTS });
  const [skipSmall, setSkipSmall] = useState(SKIP_SMALL_SEGMENTS_DEFAULT);
  const [isolatedBoost, setIsolatedBoost] = useState(ISOLATED_BOOST_DEFAULT);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [showGrid, setShowGrid] = useState(false);
  // Re-entrancy guard. `disabled={busy}` greys the button, but the heavy kernel
  // blocks the main thread, so a click landed during the freeze is queued by the
  // browser and dispatched the instant the button re-enables — starting a second
  // run. This ref rejects that queued click; it's cleared one macrotask after the
  // run finishes, after the stray click has already been processed and bounced.
  const runningRef = useRef(false);

  // Clear the grid overlay when the panel's host unmounts (e.g. leaving the map).
  useEffect(() => () => dispatchCoreGridUpdated(null), []);

  if (!open || !isCoreTuningEnabled(accessLevel)) {
    return null;
  }

  const setField = (key: keyof EqualAreaPlacerOptions, value: number) => {
    const safe = Number.isFinite(value) ? value : EQUAL_AREA_DEFAULTS[key];
    setOpts((prev) => ({ ...prev, [key]: safe }));
  };

  /** Push the colored grid overlay for the current options (or clear it). */
  const pushGrid = (show: boolean) => {
    const mission = getCurrentMission();

    if (!show || !mission) {
      dispatchCoreGridUpdated(null);

      return;
    }

    try {
      dispatchCoreGridUpdated(mission.computeCoreGrids({ ...opts, skipSmallSegments: skipSmall, isolatedBoost }));
    } catch {
      dispatchCoreGridUpdated(null);
    }
  };

  const toggleGrid = (show: boolean) => {
    setShowGrid(show);
    pushGrid(show);
  };

  const recalc = async () => {
    // Reject a re-entrant call — including a click queued while the previous run
    // froze the main thread (see runningRef).
    if (runningRef.current) {
      return;
    }

    const mission = getCurrentMission();

    if (!mission) {
      setStatus('No mission loaded');

      return;
    }

    const spec = mission.getPrimarySpec();

    if (!spec || spec.cores <= 0) {
      setStatus('spec.cores must be > 0');

      return;
    }

    const sampleZones = cleanSet(mission.getZones().getSampleZones());

    if (sampleZones.length === 0) {
      setStatus('No sample zones');

      return;
    }

    const hasPulled = sampleZones.some((sz) =>
      cleanSet(sz.getSampleSite()?.getSamples() ?? []).some((s) => cleanSet(s.getSoilCores()).some((c) => c.pulled)),
    );

    if (hasPulled) {
      setStatus('Refused: zones have pulled cores');

      return;
    }

    runningRef.current = true;
    setBusy(true);
    setStatus('Recalculating…');

    // Let the spinner + busy state paint before the heavy synchronous placement
    // work blocks the main thread (the kernel can run for several seconds).
    await nextPaint();

    try {
      addCheckpoint('Tune: auto-place cores');

      // Wipe pending cores across all sample-zone samples (same as the button).
      for (const sz of sampleZones) {
        for (const s of cleanSet(sz.getSampleSite()?.getSamples() ?? [])) {
          for (const c of cleanSet(s.getSoilCores())) {
            c.dispose();
          }
        }
      }

      const result = await mission.autoPlaceCores({ ...opts, skipSmallSegments: skipSmall, isolatedBoost });
      await mission.calculate_path_restore_cores();
      dispatchMissionUpdated();
      dispatchCoresNeedUpdate();

      if (showGrid) {
        pushGrid(true);
      }

      setStatus(
        `✓ placed ${result.placed} · skipped ${result.skippedSegments} seg / ${result.skippedZones.length} zone`,
      );
    } catch (e) {
      setStatus(`✗ ${e instanceof Error ? e.message : 'failed'}`);
    } finally {
      setBusy(false);
      // Defer clearing the guard one macrotask so a click queued during the
      // blocking run (dispatched the moment the thread frees) is rejected by the
      // re-entrancy check above instead of kicking off a second recalculation.
      setTimeout(() => {
        runningRef.current = false;
      }, 0);
    }
  };

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        top: 88,
        right: 12,
        width: 300,
        maxHeight: '85vh',
        overflowY: 'auto',
        p: 1.5,
        zIndex: 1300,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Core placement tuning
          </Typography>
          <Typography variant="caption" color="text.secondary">
            technician only · equal-area kernel
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        centerBlend (0 = CoM · 1 = pole): <b>{opts.centerBlend.toFixed(2)}</b>
      </Typography>
      <Slider
        size="small"
        min={0}
        max={1}
        step={0.05}
        value={opts.centerBlend}
        onChange={(_, v) => setField('centerBlend', v as number)}
      />

      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        capRate (equal-area gain): <b>{opts.capRate.toFixed(2)}</b>
      </Typography>
      <Slider
        size="small"
        min={0}
        max={1.5}
        step={0.05}
        value={opts.capRate}
        onChange={(_, v) => setField('capRate', v as number)}
      />

      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        spreadRate (band stagger · 0 = off): <b>{opts.spreadRate.toFixed(2)}</b>
      </Typography>
      <Slider
        size="small"
        min={0}
        max={2}
        step={0.05}
        value={opts.spreadRate}
        onChange={(_, v) => setField('spreadRate', v as number)}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 92px', gap: 0.75, alignItems: 'center', mt: 1 }}>
        {NUM_FIELDS.map((f) => (
          <React.Fragment key={f.key}>
            <Typography variant="caption" color="text.secondary">
              {f.label}
            </Typography>
            <TextField
              type="number"
              size="small"
              value={opts[f.key]}
              onChange={(e) => setField(f.key, Number(e.target.value))}
              slotProps={{ htmlInput: { min: f.min, max: f.max, step: f.step } }}
              sx={{ '& input': { py: 0.5, fontSize: 13 } }}
            />
          </React.Fragment>
        ))}
      </Box>

      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
        isolatedBoost (single-sample weight · 0 = off): <b>{isolatedBoost.toFixed(2)}</b>
      </Typography>
      <Slider
        size="small"
        min={0}
        max={0.5}
        step={0.05}
        value={isolatedBoost}
        onChange={(_, v) => setIsolatedBoost(v as number)}
      />

      <FormControlLabel
        sx={{ ml: 0, display: 'block' }}
        control={<Checkbox size="small" checked={skipSmall} onChange={(e) => setSkipSmall(e.target.checked)} />}
        label={<Typography variant="caption">Skip small segments (&lt; avg core area)</Typography>}
      />

      <FormControlLabel
        sx={{ ml: 0, display: 'block' }}
        control={<Checkbox size="small" checked={showGrid} onChange={(e) => toggleGrid(e.target.checked)} />}
        label={<Typography variant="caption">Display grid (colored cells per zone)</Typography>}
      />

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <Button variant="contained" size="small" onClick={recalc} disabled={busy} fullWidth>
          {busy ? <CircularProgress size={18} color="inherit" /> : 'Recalculate'}
        </Button>
        <Button
          variant="text"
          size="small"
          disabled={busy}
          onClick={() => {
            setOpts({ ...EQUAL_AREA_DEFAULTS });
            setSkipSmall(SKIP_SMALL_SEGMENTS_DEFAULT);
            setIsolatedBoost(ISOLATED_BOOST_DEFAULT);
            setStatus('');
          }}
        >
          Reset
        </Button>
      </Box>

      {status && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 1, color: status.startsWith('✗') ? 'error.main' : 'text.secondary' }}
        >
          {status}
        </Typography>
      )}

      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.disabled', fontSize: 10.5 }}>
        Technician-only. Baked prod defaults live in EqualAreaCorePlacer.ts.
      </Typography>
    </Paper>
  );
}
